<template>
  <div>
    <div>
      <div
        class="header-stats"
        v-if="!isLoading && testState.id && dropdownValue"
      >
        <div class="stat-col stat-col--nav">
          <bg-select
            @change="handleSelectChange"
            :options="participants"
            :value="dropdownValue"
          ></bg-select>
          <div class="navButtons">
            <bg-navigation :link="backButtonLink" icon="chevronLeft" />
            <bg-navigation :link="nextButtonLink" icon="chevronRight" />
          </div>
        </div>
        <div class="stat-col">
          <div class="stat-header">Time to complete</div>
          <div class="stat-value stat-value--inline">
            <span>{{ timeToComplete }}</span>
            <bg-tooltip
              v-if="hasTtcMismatch"
              :dark="true"
              :info="true"
              placement="top"
            >
              <template #content>
                Recording length can differ from time to complete because video and mouse data are captured by separate processes. Minor differences are expected.
              </template>
            </bg-tooltip>
          </div>
        </div>
        <div class="stat-col" v-if="data && data.total_clicks">
          <div class="stat-header">Clicks</div>
          <div class="stat-value">{{ data.total_clicks }}</div>
        </div>
        <div class="stat-col">
          <div class="stat-header">Age</div>
          <div class="stat-value" v-if="data">{{ data.demographic.age }}</div>
        </div>
        <div class="stat-col">
          <div class="stat-header">Education</div>
          <div
            class="stat-value stat-value--upper"
            v-if="data && data.demographic"
          >
            {{ data.demographic.education }}
          </div>
        </div>
        <div class="stat-col">
          <div class="stat-header">Gender</div>
          <div class="stat-value" v-if="data && data.demographic">
            {{ data.demographic.gender }}
          </div>
        </div>
      </div>
      <bg-video-chart
        v-if="!ppLoading"
        :data="data"
        :customControls="true"
        :testPlatform="testPlatform"
        @videoLoaded="onVideoLoaded"
      />
    </div>
    <bg-loader :dark="true" v-if="isLoading || ppLoading"></bg-loader>
  </div>
</template>

<style scoped>
.header-stats {
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;
}

.stat-col {
  margin-right: 50px;
  display: flex;
  flex-direction: column;
}

.stat-header {
  font-weight: 300;
}

.stat-value {
  font-weight: 700;
  font-size: 24px;
  color: var(--bg-color-text-secondary);
}

.stat-col--nav {
  flex-direction: row;
}

.navButtons {
  display: flex;
}

.navButtons a {
  margin-right: 10px;
}

.navButtons a:first-child {
  margin-left: 15px;
}
</style>

<script>
import BgTypography from "@/directives/bg-typography";
import BgSelect from "@/components/bg-select";
import BgNavigation from "@/components/bg-navigation";
import API from "@/api";
import BgVideoChart from "@/components/bg-video-chart";
import BgLoader from "@/components/bg-loader";
import moment from "moment";
import { mapState } from "vuex";

export default {
  name: "participant-video",
  components: {
    BgVideoChart,
    BgLoader,
    BgSelect,
    BgNavigation,
    BgTooltip: () => import('@/components/bg-tooltip'),
  },
  directives: {
    BgTypography,
  },
  data() {
    return {
      isLoading: true,
      ppLoading: true,
      data: null,
      participants: [],
      dropdownValue: false,
      testPlatform: "desktop",
      hasTtcMismatch: false,
      loadedVideoDuration: null,
    };
  },
  mounted() {
    API.test.getTest(this.$route.params.id).then((res) => {
      this.testPlatform = res.platform;
      this.getParticipants();
      this.participants = res.participants.map((p) => {
        return {
          value: p.id,
          label: `Participant ${p.id}`,
        };
      });
      this.dropdownValue = {
        value: this.$route.params.participantId,
        label: `Participant ${this.$route.params.participantId}`,
      };
      this.isLoading = false;
    });
  },
  methods: {
    onVideoLoaded(duration) {
      this.loadedVideoDuration = duration;
      // Compare against mouse-based TTC and set mismatch flag when >= 1 second diff
      const ttcSeconds = this.computeTtcSeconds();
      if (ttcSeconds != null && duration != null) {
        this.hasTtcMismatch = Math.abs(Math.round(duration) - Math.round(ttcSeconds)) >= 1;
      } else {
        this.hasTtcMismatch = false;
      }
    },
    getParticipants() {
      API.test
        .getParticipant(this.$route.params.id, this.$route.params.participantId)
        .then((res) => {
          this.data = res;
          this.ppLoading = false;
        });
    },
    handleSelectChange(val) {
      if (this.$route.participantId !== val) {
        this.$router.replace(
          `/test/${this.$route.params.id}/participant/${val.value}/recording`
        );
      }
    },
    computeTtcSeconds() {
      if (!this.data) return null;
      const startTime = moment(this.data.started_at);
      const endTime = moment(this.data.ended_at);
      return endTime.diff(startTime, 'seconds', true);
    },
  },
  watch: {
    $route(to, from) {
      if (to.params.participantId !== from.params.participantId) {
        this.isLoading = true;
        this.getTest();
      }
    },
  },
  computed: {
    timeToComplete() {
      if (this.data) {
        const startTime = moment(this.data.started_at);
        const endTime = moment(this.data.ended_at);
        const diff = endTime.diff(startTime);
        return moment(diff).format("mm:ss");
      } else {
        return 0;
      }
    },
    nextButtonLink() {
      const newIndex =
        this.$route.params.participantId >= this.participants.length
          ? 1
          : Number(this.$route.params.participantId) + 1;

      return `/test/${this.testState.id}/participant/${newIndex}/recording`;
    },
    backButtonLink() {
      const newIndex =
        this.$route.params.participantId == 1
          ? this.participants.length
          : Number(this.$route.params.participantId) - 1;

      return `/test/${this.testState.id}/participant/${newIndex}/recording`;
    },
    ...mapState({
      testState: (state) => state.test,
    }),
  },
};
</script>

<style scoped>
.stat-value--inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
</style>
